import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { acquireScrollLock, releaseScrollLock } from '@theme/scroll-lock';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {HTMLElement | null} */
  #galleryScroller = null;

  /** @type {number} */
  #currentIndex = 0;

  /** @type {HTMLElement | null} */
  #previouslyFocusedElement = null;

  /** @type {HTMLElement | null} */
  #zoomedMediaContainer = null;

  static #DESKTOP_ZOOM_SCALE = 2;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.#galleryScroller = this.querySelector('.dialog-zoomed-gallery');
    this.#galleryScroller?.addEventListener('scroll', this.handleScroll);
    this.#galleryScroller?.addEventListener('click', this.#handleGalleryClick);
    // Prevent browser page zoom when trackpad-pinching anywhere in the open dialog.
    this.refs.dialog.addEventListener('wheel', this.#handleDialogWheel, { passive: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('wheel', this.#handleDialogWheel);
    this.#galleryScroller?.removeEventListener('scroll', this.handleScroll);
    this.#galleryScroller?.removeEventListener('click', this.#handleGalleryClick);
    this.#galleryScroller = null;
    releaseScrollLock(this);
  }

  #lockPageScroll() {
    if (!this.refs.dialog.hasAttribute('scroll-lock')) return;

    acquireScrollLock(this);
  }

  #unlockPageScroll() {
    releaseScrollLock(this);
  }

  #setInitialDialogFocus() {
    const { dialog } = this.refs;

    if (!(dialog instanceof HTMLDialogElement)) return;

    if (!dialog.hasAttribute('tabindex')) {
      dialog.setAttribute('tabindex', '-1');
    }

    dialog.focus({ preventScroll: true });
  }

  #restoreFocus() {
    const previouslyFocused = this.#previouslyFocusedElement;
    this.#previouslyFocusedElement = null;

    if (previouslyFocused instanceof HTMLElement) {
      previouslyFocused.focus({ preventScroll: true });
    }
  }

  #isHorizontalZoomGallery() {
    return window.matchMedia(
      '(max-width: 749px), (min-width: 750px) and (max-width: 1366px) and (hover: none) and (pointer: coarse)'
    ).matches;
  }

  #isDesktopZoomGallery() {
    return window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)').matches;
  }

  #shouldAnimateZoom() {
    if (this.#isDesktopZoomGallery()) return false;

    return supportsViewTransitions() && !isLowPowerDevice() && !prefersReducedMotion();
  }

  /**
   * @param {ScrollBehavior} behavior
   * @returns {{ behavior: ScrollBehavior, block: ScrollLogicalPosition, inline: ScrollLogicalPosition }}
   */
  #getGalleryScrollOptions(behavior) {
    if (this.#isHorizontalZoomGallery()) {
      return { behavior, block: 'nearest', inline: 'start' };
    }

    return { behavior, block: 'start', inline: 'nearest' };
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    this.#previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.classList.remove('dialog-closing');
      this.#lockPageScroll();
      dialog.showModal();
      this.#setInitialDialogFocus();

      const galleryScroller = this.querySelector('.dialog-zoomed-gallery');

      for (const target of [targetThumbnail, targetImage]) {
        if (!target) continue;

        scrollIntoView(target, {
          ancestor: target === targetImage && galleryScroller instanceof HTMLElement ? galleryScroller : thumbnails,
          behavior: 'instant',
          ...(target === targetImage ? this.#getGalleryScrollOptions('instant') : { block: 'center', inline: 'center' })
        });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!this.#shouldAnimateZoom() || !sourceImage || !targetImage) {
      open();
      this.#currentIndex = index;
      this.#updateMobileCounter(index);
      return;
    }

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.#currentIndex = index;
    this.#updateMobileCounter(index);
    this.selectSlide(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);

    if (activeIndex < 0) return;

    if (this.#zoomedMediaContainer && media[activeIndex] !== this.#zoomedMediaContainer) {
      this.#resetImageZoom(this.#zoomedMediaContainer);
    }

    // Also clear zoom on any non-active slide that still has drag-zoom applied.
    for (const [index, mediaContainer] of media.entries()) {
      if (index === activeIndex || !(mediaContainer instanceof HTMLElement)) continue;
      const wrapper = mediaContainer.querySelector('drag-zoom-wrapper.is-zoomed');
      if (wrapper) {
        this.#resetImageZoom(mediaContainer);
      }
    }

    this.#currentIndex = activeIndex;
    this.#updateMobileCounter(activeIndex);

    const targetThumbnail = thumbnails.children[activeIndex];

    if (targetThumbnail instanceof HTMLElement) {
      Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
        const selected = i === activeIndex;
        button.setAttribute('data-selected', `${selected}`);
        button.removeAttribute('aria-selected');
        if (selected) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    }

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    if (this.#isDesktopZoomGallery()) {
      return this.#closeWithDrawerAnimation();
    }

    const { dialog, media } = this.refs;

    if (!this.#shouldAnimateZoom()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  async #closeWithDrawerAnimation() {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    if (!prefersReducedMotion()) {
      dialog.style.animation = 'none';
      void dialog.offsetWidth;
      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';

      await onAnimationEnd(dialog, undefined, {
        subtree: false
      });
    }

    this.closeDialog();
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.#resetImageZoom();
    dialog.classList.remove('dialog-closing');
    dialog.close();
    this.#unlockPageScroll();
    this.#restoreFocus();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Navigates to the previous slide in the zoom gallery.
   */
  previous() {
    if (this.#currentIndex <= 0) return;

    this.selectSlide(this.#currentIndex - 1);
  }

  /**
   * Navigates to the next slide in the zoom gallery.
   */
  next() {
    if (this.#currentIndex >= this.refs.media.length - 1) return;

    this.selectSlide(this.#currentIndex + 1);
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectSlide(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    return this.selectSlide(index, options);
  }

  /**
   * Selects a slide in the zoom gallery.
   * @param {number} index - The index of the slide to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectSlide(index, options = { behavior: 'smooth' }) {
    const { media, thumbnails } = this.refs;

    if (!media?.length) return;
    if (isNaN(index) || index < 0 || index >= media.length) return;

    this.#resetImageZoom();
    this.#currentIndex = index;
    this.#updateMobileCounter(index);

    if (thumbnails?.children.length) {
      const targetThumbnail = thumbnails.children[index];

      if (targetThumbnail instanceof HTMLElement) {
        Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
          const selected = i === index;
          button.setAttribute('data-selected', `${selected}`);
          button.removeAttribute('aria-selected');
          if (selected) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });

        scrollIntoView(targetThumbnail, {
          ancestor: thumbnails,
          behavior: options.behavior,
          block: 'center',
          inline: 'center'
        });
      }
    }

    const targetImage = media[index];

    if (targetImage) {
      const galleryScroller = this.querySelector('.dialog-zoomed-gallery');
      scrollIntoView(targetImage, {
        ancestor: galleryScroller instanceof HTMLElement ? galleryScroller : undefined,
        ...this.#getGalleryScrollOptions(options.behavior)
      });

      this.loadHighResolutionImage(targetImage);
    }

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Updates the mobile footer counter.
   * @param {number} index - The active slide index.
   */
  #updateMobileCounter(index) {
    const value = String(index + 1);

    for (const currentIndex of this.querySelectorAll('.dialog-zoomed-gallery__current-index')) {
      currentIndex.textContent = value;
    }
  }

  #handleDialogWheel = (event) => {
    if (!event.ctrlKey) return;
    event.preventDefault();
  };

  #handleGalleryClick = (event) => {
    if (!this.#isDesktopZoomGallery()) return;
    if (!(event.target instanceof Element)) return;

    const image = event.target.closest('.product-media__image');
    if (!image) return;

    const container = image.closest('.product-media-container--image');
    if (!(container instanceof HTMLElement) || !this.refs.dialog.contains(container)) return;

    const wrapper = container.querySelector('drag-zoom-wrapper');
    if (!(wrapper instanceof HTMLElement) || !('zoomToward' in wrapper) || !('resetZoom' in wrapper)) return;

    event.preventDefault();

    const dragZoom = /** @type {import('./drag-zoom-wrapper').DragZoomWrapper} */ (wrapper);

    if (dragZoom.scale > 1) {
      // Trackpad-pinch zoom is panned/pinched out — don't treat click as reset.
      if (dragZoom.isPinchZoomed) return;

      dragZoom.resetZoom();
      this.#zoomedMediaContainer = null;
      return;
    }

    this.#resetImageZoom();
    dragZoom.zoomToward(ZoomDialog.#DESKTOP_ZOOM_SCALE, event.clientX, event.clientY);
    this.#zoomedMediaContainer = container;
    this.loadHighResolutionImage(container);
  };

  /**
   * @param {HTMLElement} [container]
   */
  #resetImageZoom(container) {
    const containers = container ? [container] : this.refs.media;

    for (const mediaContainer of containers) {
      if (!(mediaContainer instanceof HTMLElement)) continue;

      mediaContainer.classList.remove('product-media-container--zoomed-in');
      mediaContainer.style.removeProperty('--dialog-zoom-origin-x');
      mediaContainer.style.removeProperty('--dialog-zoom-origin-y');
      mediaContainer.style.removeProperty('--dialog-zoom-scale');

      const wrapper = mediaContainer.querySelector('drag-zoom-wrapper');
      if (wrapper instanceof HTMLElement && 'resetZoom' in wrapper) {
        /** @type {import('./drag-zoom-wrapper').DragZoomWrapper} */ (wrapper).resetZoom();
      }
    }

    if (!container || container === this.#zoomedMediaContainer) {
      this.#zoomedMediaContainer = null;
    }
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100)
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
